// StepCompliance — step 4 of the wizard. Two things get collected:
//   1. Regulatory frameworks (HIPAA, SOC2, …): the grid is PRE-FILLED from
//      auto-detection of the problem statement, and stays in sync with it
//      UNTIL the user toggles a card — at which point `regulatoryUserEdited`
//      flips true and the parent stops overwriting their choices. Cards that
//      were auto-detected show a small "Detected" marker.
//   2. AI-governance disposition ("in scope" / "not applicable"): if this
//      project produces AI output, a Phase-5 export gate demands a signed
//      disposition. Ratifying it here pre-empts that gate.
// Both selections ARE sent to the backend in the intake payload.
import { ShieldCheck } from 'lucide-react'

import { REGULATORY_CUES } from '@/components/intake/shapes'
import type { RegulatoryFlag } from '@/types'
import type { Detected, NewProjectForm, UpdateForm } from '../types'

export function StepCompliance({
  form,
  update,
  detected,
}: {
  form: NewProjectForm
  update: UpdateForm
  detected: Detected
}) {
  const cues = REGULATORY_CUES.filter((c) => c.flag !== 'none')
  const detectedFlags = new Set(detected.regulatory.map((r) => r.flag))

  const toggle = (flag: RegulatoryFlag) => {
    const has = form.regulatory.includes(flag)
    const next = has
      ? form.regulatory.filter((f) => f !== flag)
      : [...form.regulatory.filter((f) => f !== 'none'), flag]
    update({ regulatory: next, regulatoryUserEdited: true })
  }

  const setDisposition = (d: 'in_scope' | 'not_applicable') =>
    update({
      ai_governance_disposition: form.ai_governance_disposition === d ? null : d,
    })

  return (
    <>
      <header className="step-header">
        <div className="step-section-tag">
          <ShieldCheck size={14} />
          Compliance Cues
        </div>
        <h1 className="step-h1">Which <em>regulations</em> apply to this project?</h1>
        <p className="step-lede">
          Pick every framework this project touches — the <span className="accent">Compliance agent</span>{' '}
          builds a risk register and evidence checklist for each one. Anything we detected from your
          description is pre-selected.
        </p>
      </header>

      <div className="check-grid">
        {cues.map((c) => {
          const isSelected = form.regulatory.includes(c.flag)
          return (
            <button
              key={c.flag}
              type="button"
              className={`check-card${isSelected ? ' selected' : ''}`}
              onClick={() => toggle(c.flag)}
            >
              <div className="check-card-head">
                <span className="check-card-title">{c.label}</span>
                <span className="check-box" />
              </div>
              {detectedFlags.has(c.flag) && (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--x-mid)' }}>
                  Detected
                </span>
              )}
            </button>
          )
        })}
      </div>

      <section className="gov-stack">
        <div className="gov-head">
          <div className="gov-tag">
            AI Governance · Ratification
            {detected.shape.shape?.id === 'ml_ai' && <span className="detected">+ System Detected</span>}
          </div>
          {/* PEG-AI-GOVERNANCE-SIGNATURE-001 is a Phase-5 export-gate rule
              that blocks the doc until an AI-governance disposition is signed.
              Picking one here satisfies it ahead of time. */}
          <span className="gov-aside">Closes PEG-AI-GOVERNANCE-SIGNATURE-001 proactively</span>
        </div>
        <p className="gov-body">
          If this project produces an <b>AI-derived output</b> (RAG / model / predictive / agentic / ML),
          the export gate fires unless a disposition is signed. Ratify it here to pre-empt it — both
          options are <em>signed</em> in scope.
        </p>

        <div className="gov-pair">
          <button
            type="button"
            className={`gov-option${form.ai_governance_disposition === 'in_scope' ? ' selected' : ''}`}
            onClick={() => setDisposition('in_scope')}
          >
            <div className="gov-option-head">
              <div className="cadence-radio" />
              <div className="gov-option-title">In scope</div>
            </div>
            <div className="gov-option-desc">
              We'll apply an AI governance program (NIST AI RMF, model card, bias/drift monitoring, etc.).
            </div>
          </button>
          <button
            type="button"
            className={`gov-option${form.ai_governance_disposition === 'not_applicable' ? ' selected' : ''}`}
            onClick={() => setDisposition('not_applicable')}
          >
            <div className="gov-option-head">
              <div className="cadence-radio" />
              <div className="gov-option-title">Not applicable</div>
            </div>
            <div className="gov-option-desc">
              The AI element doesn't trigger governance obligations in this scope (architect affirms).
            </div>
          </button>
        </div>

        {form.ai_governance_disposition && (
          <div className="gov-ratify">
            <div className="form-card-grid">
              <div>
                <label className="form-field-label" htmlFor="npw-ratifier">
                  Ratified by <span className="req">*</span>
                </label>
                <input
                  id="npw-ratifier"
                  className="form-input"
                  type="text"
                  placeholder="e.g. Jane Doe, Head of AI Governance"
                  value={form.ai_governance_ratified_by}
                  onChange={(e) => update({ ai_governance_ratified_by: e.target.value })}
                />
              </div>
              <div>
                <label className="form-field-label" htmlFor="npw-rationale">Rationale</label>
                <input
                  id="npw-rationale"
                  className="form-input"
                  type="text"
                  placeholder="Optional — why this disposition holds"
                  value={form.ai_governance_rationale}
                  onChange={(e) => update({ ai_governance_rationale: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  )
}
